import { StyledCard } from '@common/Configuration/FormValueConfiguration';
import { Entity } from '@common/types/Entity';
import { theme } from '@prophecy/theme';
import { Stack, Tooltip } from '@prophecy/ui-v3';
import { clone } from '@prophecy/utils/data';
import React, { useEffect, useRef } from 'react';

import { JobEntityType, TranspilerOptionsType } from '../../../../../data/apis/transpileTypes';
import { FileFormats } from '../../../config';
import { useImportState } from '../../useImportState';
import { StyledStackContainer } from '../styled';
import { ActionTypes, FileTypes } from '../types';
import { DeleteEntityDropdown, EntityRootHeading, getEntityErrorTitle, getValidateIcon, useToggleMap } from '../utils';
import { AssociatedEntity } from './AssociatedEntity';
import { EntityFileAndName } from './EntityFileAndName';
import { EntityHeading } from './EntityHeading';
import { TranspilerOptions } from './TranspilerOptions';

export const EntityJobs = ({
  jobs = [],
  hasPipelines: _hasPipelines
}: {
  jobs?: JobEntityType[];
  hasPipelines: boolean;
}) => {
  const {
    transpilerState: { jobWithErrors },
    transpileGraphState: {
      clientState: {
        validate: { validatedData }
      }
    },
    formDataWithActions: {
      formContext: { getValues }
    },
    actions: { updateTranspilerJob }
  } = useImportState();

  const { toggleMap, handleToggleEntityState } = useToggleMap();
  const source = getValues('source');

  const fileType = FileTypes.plan;
  const isAbinitio = source === FileFormats.abinitio;
  const jobCount = jobs.length;
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (jobCount > 0 && container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [containerRef, jobCount]);

  if (jobCount < 1) {
    return null;
  }

  const handleJobChange = (value: string | boolean, index: number, key: string) => {
    const currentJob = jobs[index];
    updateTranspilerJob({ ...currentJob, [key]: value }, currentJob.__pathIndex as number);
  };

  const isDisabled = jobs.some((job) => !job.enabled);

  const handleOptionChange = (
    value: string,
    index: number,
    jobIndex: number,
    key: keyof TranspilerOptionsType,
    action?: ActionTypes
  ) => {
    const currentJob = jobs[jobIndex];
    const currentJobOptions = currentJob?.transpilerOptions || { targetPsets: [] };
    const currentValues = clone(currentJobOptions?.[key] || []);
    if (action === ActionTypes.delete) {
      currentValues.splice(index, 1);
    } else if (action === ActionTypes.add) {
      currentValues.push(value);
    } else {
      currentValues[index] = value;
    }
    updateTranspilerJob(
      {
        ...jobs[jobIndex],
        transpilerOptions: { ...currentJobOptions, [key]: currentValues }
      },
      currentJob.__pathIndex as number
    );
  };
  return (
    <Stack width='100%' grow='1' gap={theme.spaces.x16}>
      <EntityRootHeading
        disabled={isDisabled}
        onDelete={() => updateTranspilerJob(jobs[0], 0, ActionTypes.deleteAll)}
        count={jobs.length}
        entity={Entity.Job}
        onChange={(checked) => updateTranspilerJob(jobs[0], 0, ActionTypes.all, checked)}
      />
      <StyledStackContainer ref={containerRef} gap={theme.spaces.x16} grow='1'>
        {jobs.map((job, index) => {
          const toggleKey = 'job_' + index;
          const error = jobWithErrors.find((p) => p.name === job.name);
          const options = job.transpilerOptions;
          const validatedJob = validatedData?.jobs?.find((job1) => job1.name === job.name);
          const validateStatus = validatedJob?.validationStatus;
          const message = validatedJob?.errorMessage;
          const hasError = !!error;
          return (
            <Tooltip key={index + 'job'} title={getEntityErrorTitle(error)}>
              <Stack gap={theme.spaces.x16}>
                <StyledCard hasError={hasError}>
                  <Stack gap={theme.spaces.x16} data-entityname={`${Entity.Job}_${job.name}`}>
                    <EntityHeading
                      entity={Entity.Job}
                      enabled={job.enabled}
                      onChange={(value, key) => handleJobChange(value, index, key)}
                      title={job.name}
                      actions={
                        <Stack direction='horizontal' gap={theme.spaces.x6} alignY='center'>
                          {validateStatus !== undefined && getValidateIcon(validateStatus, message)}
                          <DeleteEntityDropdown
                            title='Delete Pipeline'
                            name={job.name}
                            onClick={() => updateTranspilerJob(job, job.__pathIndex as number, ActionTypes.delete)}
                          />
                        </Stack>
                      }
                      hasToggle={!toggleMap[toggleKey]}
                      toggle={() => handleToggleEntityState(toggleKey)}
                    />
                    {toggleMap[toggleKey] ? null : (
                      <>
                        <EntityFileAndName
                          error={error}
                          filePathValue={job.filePath}
                          targetValue={job.name}
                          type={fileType}
                          onChange={(value, key) => handleJobChange(value, index, key)}
                          targetName={'Target Job Name'}
                        />
                        {isAbinitio ? (
                          <>
                            <TranspilerOptions
                              parentPsetPath={job.filePath}
                              heading={'Default Configuration Psets (*.psets file)'}
                              values={options?.targetPsets || []}
                              onChange={(value, valueIndex, action) =>
                                handleOptionChange(value, valueIndex, index, 'targetPsets', action)
                              }
                            />
                          </>
                        ) : null}
                        <AssociatedEntity parentEntity={Entity.Job} entity={Entity.Pipeline} path={job.filePath} />
                      </>
                    )}
                  </Stack>
                </StyledCard>
              </Stack>
            </Tooltip>
          );
        })}
      </StyledStackContainer>
    </Stack>
  );
};
